'use strict';

const { EMPTY_STRING, NOT_AVAILABLE_STATUS } = require('../../constants');
const { ISPU_INFO_MESSAGE_SELECTOR, SHIP_TO_INFO_MESSAGE_SELECTOR, STORE_AVAILABILITY_MESSAGE_SELECTOR, STORE_SERVICE_INFO_MESSAGE_SELECTOR, LOW_STOCK_ISPU_SELECTOR } = require('./constants');
const { injectMessageAndUpdateElementState } = require('./messagingUtil');
const { queryFirst } = require('../../domUtil');

/**
 * Function to get ISPU non-availability element
 * @param {HTMLElement} productContainer - Product container Element
 * @returns {HTMLElement} ISPU non-availability element
 */
function getISPUNotAvailableMessageEl(productContainer) {
    return queryFirst(ISPU_INFO_MESSAGE_SELECTOR, productContainer);
}

/**
 * Function to get Ship-To address non-availability element
 * @param {HTMLElement} productContainer - Product container Element
 * @returns {HTMLElement} Ship-To non-availability element
 */
function getShipToNotAvailableMessageEl(productContainer) {
    return queryFirst(SHIP_TO_INFO_MESSAGE_SELECTOR, productContainer);
}

/**
 * Function to get ISPU inventory level message element ('Low stock')
 * @param {HTMLElement} productContainer - Product container Element
 * @returns {HTMLElement} ISPU inventory level message element
 */
function getISPUInventoryLevelMessageEl(productContainer) {
    return queryFirst(STORE_AVAILABILITY_MESSAGE_SELECTOR, productContainer);
}

/**
 * Function to get ISPU inventory level message element above Add To Tote ('Low stock')
 * @param {HTMLElement} productContainer - Product container Element
 * @returns {HTMLElement} ISPU low inventory level message element
 */
 function getISPULowInventoryMessageEl(productContainer) {
    return queryFirst(LOW_STOCK_ISPU_SELECTOR, productContainer);
}

/**
 * Function to get ISPU service response messaging element ('No stores found')
 * @param {HTMLElement} productContainer - Product container Element
 * @returns {HTMLElement} ISPU inventory level message element
 */
function getStoreServiceResponseMessageEl(productContainer) {
    return queryFirst(STORE_SERVICE_INFO_MESSAGE_SELECTOR, productContainer);
}

/**
 * Function to inject Ship to address availability messages within shipping preferences section
 * @param {string} shipToAvailabilityStatus - Ship to address availability status
 * @param {Object} messages - global messages object
 * @param {HTMLElement} productContainer - Product container Element
 */
function updateShipToInventoryMessages(shipToAvailabilityStatus, messages, productContainer) {
    // Updating ship to address inventory messages
    const shipToAvailableMessageEl = getShipToNotAvailableMessageEl(productContainer);
    if (shipToAvailableMessageEl) {
        injectMessageAndUpdateElementState(shipToAvailableMessageEl, shipToAvailabilityStatus === NOT_AVAILABLE_STATUS ? messages.notAvailableShipToMessage : EMPTY_STRING);
    }
}

/**
 * Function to inject ISPU availability messages within shipping preferences section
 * @param {string} ispuAvailabilityStatus - In store pick up inventory availability status
 * @param {boolean} isStorePickUpLowInventory - Is ISPU has low inventory
 * @param {string} ispuAvailabilityMessage - In store pick up inventory message
 * @param {Object} messages - global messages object
 * @param {HTMLElement} productContainer - Product container Element
 */
function updateISPUInventoryMessages(ispuAvailabilityStatus, isStorePickUpLowInventory = false, ispuAvailabilityMessage = EMPTY_STRING, ispuLowInventoryMessage = EMPTY_STRING, messages, productContainer) {
    // Updating ISPU inventory messages
    const ispuNotAvailableMessageEl = getISPUNotAvailableMessageEl(productContainer);
    const ispuInventoryLevelMessageEl = getISPUInventoryLevelMessageEl(productContainer);
    const ispuLowInventoryMessageEl = getISPULowInventoryMessageEl(productContainer);
    if (ispuAvailabilityStatus !== NOT_AVAILABLE_STATUS) {
        injectMessageAndUpdateElementState(ispuNotAvailableMessageEl, EMPTY_STRING);
        injectMessageAndUpdateElementState(ispuInventoryLevelMessageEl, isStorePickUpLowInventory ? ispuAvailabilityMessage : EMPTY_STRING);
        injectMessageAndUpdateElementState(ispuLowInventoryMessageEl, isStorePickUpLowInventory ? ispuLowInventoryMessage : EMPTY_STRING);
    } else {
        injectMessageAndUpdateElementState(ispuNotAvailableMessageEl, messages.notAvailableISPUMessage);
        injectMessageAndUpdateElementState(ispuInventoryLevelMessageEl, EMPTY_STRING);
        injectMessageAndUpdateElementState(ispuLowInventoryMessageEl, EMPTY_STRING);
    }
}

/**
 * Function to inject service response messages
 * @param {Object} message - message to be injected
 * @param {boolean} wrapText - a flag to wrap the text conditionally
 * @param {HTMLElement} productContainer - Product container Element
 */
function updateServiceResponseMessage(message, wrapText, productContainer) {
    const ispuServiceResponseEl = getStoreServiceResponseMessageEl(productContainer);
    message = wrapText ? `<span class="text">${message}</span>` : message;
    injectMessageAndUpdateElementState(ispuServiceResponseEl, message);
}

/**
 * Function to clear availability messages within shipping preferences section
 * @param {HTMLElement} productContainer - Product container Element
 */
function clearAllInventoryMessages(productContainer) {
    const ispuNotAvailableMessageEl = getISPUNotAvailableMessageEl(productContainer);
    injectMessageAndUpdateElementState(ispuNotAvailableMessageEl, EMPTY_STRING);

    const ispuInventoryLevelMessageEl = getISPUInventoryLevelMessageEl(productContainer);
    injectMessageAndUpdateElementState(ispuInventoryLevelMessageEl, EMPTY_STRING);

    const shipToAvailableMessageEl = getShipToNotAvailableMessageEl(productContainer);
    injectMessageAndUpdateElementState(shipToAvailableMessageEl, EMPTY_STRING);
}

/**
 * Function to clear service response messages
 * @param {HTMLElement} productContainer - Product container Element
 */
function clearServiceResponseMessage(productContainer) {
    const ispuServiceResponseEl = getStoreServiceResponseMessageEl(productContainer);
    injectMessageAndUpdateElementState(ispuServiceResponseEl, EMPTY_STRING);
}

module.exports = {
    updateShipToInventoryMessages,
    updateISPUInventoryMessages,
    clearAllInventoryMessages,
    updateServiceResponseMessage,
    clearServiceResponseMessage
};
